import { Subject } from 'rxjs';
import { IBiAddMediaFileInfo } from '../../../constants/Bi';
import { MainSource, Source } from '../../../constants/Source';
import { IWithFolderPathInner } from '../../hoc/withFolderPath/withFolderPath.types';
import {
  MediaManagerSDKFile,
  MediaManagerSDKFolder,
  MediaManagerSDKTotalProgress,
  MediaManagerSDKUploadingFile,
} from '../../media-manager-sdk';

export interface IFilesUploaderContext {
  queueStartObservable: Subject<MediaManagerSDKUploadingFile[]>;
  queueChangeObservable: Subject<MediaManagerSDKUploadingFile[]>;
  queueFinishedObservable: Subject<MediaManagerSDKUploadingFile[]>;
  itemUploadedObservable: Subject<MediaManagerSDKUploadingFile>;
  folderUploadedObservable: Subject<MediaManagerSDKFolder>;

  importOperations: Map<string, IImportOperation>;

  cancelAllUploads: () => void;
  clearUploadingQueue: () => void;
  removeFileFromUploadingQueue: (file: MediaManagerSDKUploadingFile) => void;

  uploadingFiles: MediaManagerSDKUploadingFile[];
  uploadedFiles: MediaManagerSDKUploadingFile[];
  invalidFiles: MediaManagerSDKUploadingFile[];
  failedFiles: MediaManagerSDKUploadingFile[];

  filesCount: number;
  notFailedFilesCount: number;
  notAbortedFilesCount: number;
  uploadedFilesCount: number;
  invalidFilesCount: number;
  failedFilesCount: number;

  hasFailedFiles: boolean;
  hasAbortableFiles: boolean;
  hasNetworkErrorResumableFiles: boolean;
  hasServerErrorAutomaticResumeFile: boolean;
  hasUploadingFiles: boolean;
  areAllFilesUploaded: boolean;
}

export type TUploaderReducerState = MediaManagerSDKUploadingFile[];

export enum UploadStatus {
  Uploading = 'uploading',
  Waiting = 'waiting',
  Succeeded = 'succeeded',
  Processing = 'processing',
  Failed = 'failed',
  Aborted = 'aborted',
}

export enum UploaderAction {
  UpdateQueue,
  UpdateFileProgress,
  ClearQueue,
}

export enum FileOrigin {
  Uploaded = 'uploaded',
  VideoMaker = 'vm',
  MediaStudio = 'media_studio',
  Shutterstock = 'shutterStock',
  Unsplash = 'unsplash',
  WixStock = 'wixstock',
  WixVideoLibrary = 'Wix Video Library',
  AdobeExpress = 'adobe',
}

export type TUploaderReducerPayload = Partial<{
  uploadingFiles: MediaManagerSDKUploadingFile[];
  uploadingFile: MediaManagerSDKUploadingFile;
  updatedItem: Partial<MediaManagerSDKFile>;
  progress: MediaManagerSDKTotalProgress;
}>;

export type TUploaderReducerAction = {
  type: UploaderAction;
  payload?: TUploaderReducerPayload;
};

export type TUploadFiles = (
  files: FileList,
  folderPath?: MediaManagerSDKFolder | MediaManagerSDKFolder[],
  options?: IUploadFilesMPOptions,
) => void;

export type TImportFiles = (files: MediaManagerSDKFile[], options: IUploadFilesMPOptions) => void;

export interface IImportOperation {
  onDone: (data: object) => void;
  onFail?: (data: object) => void;
}

export type TEncodingOptions = {
  profile_name?: string;
  fast_transcode?: boolean;
};

export interface IFileBiParams {
  files: Map<object, IBiAddMediaFileInfo>;
}

export interface IUploadFilesMPOptions {
  bi?: IFileBiParams;
  source?: Source;
  tags?: string[];
}

export interface IOuter {
  uploadingFiles?: MediaManagerSDKUploadingFile[];
}

export interface IUseRedirectAfterFirstFileUploaded {
  openFolderById: IWithFolderPathInner['openFolderById'];
  source: MainSource;
}

export interface TUseUploadFilesHandlersOuter {
  source: MainSource;
  folderPath: MediaManagerSDKFolder[];
}

export interface TUseUploadFilesHandlersHandlers {
  uploadFiles: (
    files: FileList | File[],
    uploadFolderPath?: MediaManagerSDKFolder | MediaManagerSDKFolder[],
    options?: IUploadFilesMPOptions,
  ) => void;
  uploadFilesByUrl: (
    files: MediaManagerSDKFile[],
    uploadFolderPath: MediaManagerSDKFolder[],
    options?: IUploadFilesMPOptions,
  ) => void;
  importToCurrentSite: (
    files: MediaManagerSDKFile[],
    uploadFolderPath: MediaManagerSDKFolder[],
    options?: IUploadFilesMPOptions,
  ) => void;
  importFiles: (files: MediaManagerSDKFile[], options?: IUploadFilesMPOptions) => void;
}

export type TUseUploadFilesHandlers = (params: TUseUploadFilesHandlersOuter) => TUseUploadFilesHandlersHandlers;

export interface IRedirectAfterFirstFileUploadedContext {
  getDidRedirectToPrivateMedia: () => boolean;
  setDidRedirectToPrivateMedia: (didRedirectToPrivateMedia: boolean) => void;
}
